const SOURCE_PRESENTATIONS = Object.freeze({
  deterministic: Object.freeze({
    labelKey: "Deterministic engine",
    descriptionKey: "Price and stay rules were calculated by the deterministic engine.",
    tone: "success",
  }),
  ai_explanation: Object.freeze({
    labelKey: "AI-assisted explanation",
    descriptionKey: "AI explains the recommendation; price and stay rules remain deterministic.",
    tone: "soft",
  }),
  ai_numeric_validated: Object.freeze({
    labelKey: "Validated AI number",
    descriptionKey: "The AI number passed deterministic limits and post-validation.",
    tone: "warn",
  }),
  deterministic_fallback: Object.freeze({
    labelKey: "Deterministic fallback",
    descriptionKey: "The AI result was not used and the deterministic result was kept.",
    tone: "danger",
  }),
});

const UNKNOWN_PRESENTATION = Object.freeze({
  labelKey: "Origin not verified",
  descriptionKey: "This historical row has no verifiable recommendation origin.",
  tone: "danger",
});

const VALIDATION_STATUS_LABELS = Object.freeze({
  not_requested: "Not requested",
  accepted: "Accepted",
  rejected: "Rejected",
  unavailable: "Unavailable",
  batch_error: "Batch error",
  missing_row: "Missing row",
});

const FALLBACK_VALIDATION_STATUSES = new Set([
  "rejected",
  "unavailable",
  "batch_error",
  "missing_row",
]);
const SHA256_PATTERN = /^[0-9a-f]{64}$/;

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function normalizeIdentifier(value, maxLength = 120) {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength) return null;
  return normalized;
}

function normalizeCodeArray(value) {
  if (value == null) {
    return { present: false, valid: true, values: [] };
  }
  if (!Array.isArray(value) || value.length > 50) {
    return { present: true, valid: false, values: [] };
  }

  let valid = true;
  const values = [];
  const seen = new Set();
  value.forEach((entry) => {
    const normalized = normalizeIdentifier(entry, 80);
    if (!normalized) {
      valid = false;
      return;
    }
    if (!seen.has(normalized)) {
      seen.add(normalized);
      values.push(normalized);
    }
  });

  return { present: true, valid, values };
}

function normalizeAiProvenance(value) {
  if (value == null) return { present: false, valid: true, value: null };
  if (!isPlainObject(value)) return { present: true, valid: false, value: null };

  const anomalyCodes = normalizeCodeArray(value.anomaly_codes);
  const attempted = typeof value.attempted === "boolean" ? value.attempted : null;
  const accepted = typeof value.accepted === "boolean" ? value.accepted : null;
  const mode = value.mode === "explanation" || value.mode === "numeric" ? value.mode : null;
  const provider = normalizeIdentifier(value.provider, 80);
  const model = normalizeIdentifier(value.model, 120);
  const promptVersion = normalizeIdentifier(value.prompt_version, 120);
  const hasNumericDeltaPct = Object.prototype.hasOwnProperty.call(
    value,
    "numeric_delta_pct"
  );
  const numericDeltaPct =
    typeof value.numeric_delta_pct === "number" &&
    Number.isFinite(value.numeric_delta_pct) &&
    value.numeric_delta_pct >= 0
      ? value.numeric_delta_pct
      : null;
  const numericDeltaValid =
    mode === "explanation"
      ? !hasNumericDeltaPct
      : accepted === true
      ? hasNumericDeltaPct && numericDeltaPct != null
      : !hasNumericDeltaPct || numericDeltaPct != null;
  const hasRequestHash = Object.prototype.hasOwnProperty.call(value, "request_hash");
  const hasResponseHash = Object.prototype.hasOwnProperty.call(value, "response_hash");
  const requestHash =
    typeof value.request_hash === "string" && SHA256_PATTERN.test(value.request_hash)
      ? value.request_hash
      : null;
  const responseHash =
    typeof value.response_hash === "string" && SHA256_PATTERN.test(value.response_hash)
      ? value.response_hash
      : null;
  const hashesValid =
    (!hasRequestHash || requestHash != null) &&
    (!hasResponseHash || responseHash != null) &&
    (!hasResponseHash || hasRequestHash) &&
    (accepted !== true || (requestHash != null && responseHash != null));

  return {
    present: true,
    valid:
      anomalyCodes.valid &&
      attempted != null &&
      accepted != null &&
      mode != null &&
      provider === "openai" &&
      model != null &&
      promptVersion != null &&
      numericDeltaValid &&
      hashesValid,
    value: {
      attempted,
      accepted,
      mode,
      provider,
      model,
      promptVersion,
      anomalyCodes: anomalyCodes.values,
      numericDeltaPct,
      numericDeltaPresent: hasNumericDeltaPct,
      requestHash,
      responseHash,
      requestHashPresent: hasRequestHash,
      responseHashPresent: hasResponseHash,
    },
  };
}

function isSourceMetadataVerified(source, validationStatus, provenance, rejectionCodes) {
  if (source === "deterministic") {
    return (
      validationStatus === "not_requested" &&
      !provenance.present &&
      !rejectionCodes.present
    );
  }

  if (!provenance.valid || !provenance.value || !rejectionCodes.valid) return false;
  const metadata = provenance.value;

  if (source === "ai_explanation") {
    return (
      metadata.attempted === true &&
      metadata.accepted === true &&
      metadata.mode === "explanation" &&
      metadata.numericDeltaPresent === false &&
      metadata.requestHash != null &&
      metadata.responseHash != null &&
      validationStatus === "accepted" &&
      rejectionCodes.present &&
      rejectionCodes.values.length === 0
    );
  }

  if (source === "ai_numeric_validated") {
    return (
      metadata.attempted === true &&
      metadata.accepted === true &&
      metadata.mode === "numeric" &&
      metadata.numericDeltaPresent === true &&
      metadata.numericDeltaPct != null &&
      metadata.requestHash != null &&
      metadata.responseHash != null &&
      validationStatus === "accepted" &&
      rejectionCodes.present &&
      rejectionCodes.values.length === 0
    );
  }

  if (source === "deterministic_fallback") {
    const isConfigRejection =
      rejectionCodes.values.length === 1 &&
      ["invalid_ai_mode", "invalid_numeric_delta_limit"].includes(
        rejectionCodes.values[0]
      );
    const hashesVerified =
      (validationStatus === "unavailable" &&
        !metadata.requestHashPresent &&
        !metadata.responseHashPresent) ||
      (validationStatus === "rejected" &&
        isConfigRejection &&
        !metadata.requestHashPresent &&
        !metadata.responseHashPresent) ||
      (["rejected", "missing_row"].includes(validationStatus) &&
        !isConfigRejection &&
        metadata.requestHash != null &&
        metadata.responseHash != null) ||
      (validationStatus === "batch_error" && metadata.requestHash != null);
    return (
      metadata.attempted === true &&
      metadata.accepted === false &&
      FALLBACK_VALIDATION_STATUSES.has(validationStatus) &&
      hashesVerified &&
      rejectionCodes.present &&
      rejectionCodes.values.length > 0
    );
  }

  return false;
}

export function resolveRecommendationProvenance(row) {
  const rawSource = normalizeIdentifier(row?.recommendation_source, 80);
  const source = Object.prototype.hasOwnProperty.call(SOURCE_PRESENTATIONS, rawSource)
    ? rawSource
    : null;
  const rawValidationStatus = normalizeIdentifier(row?.ai_validation_status, 80);
  const validationStatus = Object.prototype.hasOwnProperty.call(
    VALIDATION_STATUS_LABELS,
    rawValidationStatus
  )
    ? rawValidationStatus
    : null;
  const rejectionCodes = normalizeCodeArray(row?.ai_rejection_codes);
  const provenance = normalizeAiProvenance(row?.ai_provenance);
  const verified = isSourceMetadataVerified(
    source,
    validationStatus,
    provenance,
    rejectionCodes
  );
  const safeProvenance = provenance.valid ? provenance.value : null;
  const hasVerifiedRejectionMetadata = Boolean(
    FALLBACK_VALIDATION_STATUSES.has(validationStatus) &&
      rejectionCodes.valid &&
      rejectionCodes.present &&
      rejectionCodes.values.length > 0 &&
      safeProvenance?.attempted === true &&
      safeProvenance?.accepted === false &&
      isSourceMetadataVerified(
        "deterministic_fallback",
        validationStatus,
        provenance,
        rejectionCodes
      )
  );
  const rejected =
    (source === "deterministic_fallback" && verified) ||
    hasVerifiedRejectionMetadata;
  const presentation = verified
    ? SOURCE_PRESENTATIONS[source]
    : UNKNOWN_PRESENTATION;
  const auditableProvenance =
    verified || hasVerifiedRejectionMetadata ? safeProvenance : null;

  return {
    source,
    validationStatus,
    validationStatusLabelKey: validationStatus
      ? VALIDATION_STATUS_LABELS[validationStatus]
      : null,
    verified,
    rejected,
    presentation,
    rejectionCodes:
      rejected && rejectionCodes.valid ? rejectionCodes.values : [],
    anomalyCodes: auditableProvenance?.anomalyCodes || [],
    provider: auditableProvenance?.provider || null,
    model: auditableProvenance?.model || null,
    promptVersion: auditableProvenance?.promptVersion || null,
    numericDeltaPct: auditableProvenance?.numericDeltaPct ?? null,
    requestHash: auditableProvenance?.requestHash || null,
    responseHash: auditableProvenance?.responseHash || null,
  };
}
